import {
  PresentationEnvelopeCollectionDecodeResult,
  PresentationEnvelopeContract,
  PresentationEnvelopeContractCodec,
} from '@automatick/shared-ng';
import {
  isCanonicalUuid,
  isObjectRecord,
  readCanonicalProperty,
} from '@automatick/shared-client-ts';
import {
  ConversationPulseKind,
  ConversationPulseSource,
  RealtimeAudienceScope,
  RealtimeRenderDisplayKind,
  RealtimeRenderDisplayStageLabel,
  RealtimeRenderEventStage,
  RealtimeRenderEventType,
  RealtimeRenderSeverity,
  RealtimeRenderState,
} from '@automatick/shared-ts';
import { WebChatPulseMetadata } from './web-chat.models';

export { isObjectRecord } from '@automatick/shared-client-ts';

const presentationEnvelopeCodec = new PresentationEnvelopeContractCodec();
const realtimeRenderEventTypes = new Set<string>(
  Object.values(RealtimeRenderEventType),
);
const realtimeRenderEventStages = new Set<string>(
  Object.values(RealtimeRenderEventStage),
);
const realtimeRenderDisplayKinds = new Set<string>(
  Object.values(RealtimeRenderDisplayKind),
);
const realtimeRenderDisplayStageLabels = new Set<string>(
  Object.values(RealtimeRenderDisplayStageLabel),
);
const realtimeAudienceScopes = new Set<string>(
  Object.values(RealtimeAudienceScope),
);
const realtimeRenderStates = new Set<string>(
  Object.values(RealtimeRenderState),
);
const realtimeRenderSeverities = new Set<string>(
  Object.values(RealtimeRenderSeverity),
);
const conversationPulseKinds = new Set<string>(
  Object.values(ConversationPulseKind),
);
const conversationPulseSources = new Set<string>(
  Object.values(ConversationPulseSource),
);
const pulseMetadataPropertyNames = [
  'pulseKind',
  'pulseSource',
  'messageKey',
  'locale',
  'variantKey',
  'isFallback',
  'isDynamic',
  'silenceBudgetSeconds',
  'elapsedSinceLastMeaningfulPulseMs',
  'operationKey',
  'instructionId',
  'correlationId',
] as const;
const pulseMetadataPropertyNamesByFoldedName = new Map<string, string>(
  pulseMetadataPropertyNames.map((propertyName) => [
    propertyName.toLowerCase(),
    propertyName,
  ]),
);

export function readRecordProperty(
  value: object,
  propertyName: string,
): unknown {
  return readCanonicalProperty(value, propertyName);
}

export function isWebChatRenderEventType(
  value: unknown,
): value is RealtimeRenderEventType {
  return typeof value === 'string' && realtimeRenderEventTypes.has(value);
}

export function isWebChatRenderEventStage(
  value: unknown,
): value is RealtimeRenderEventStage {
  return typeof value === 'string' && realtimeRenderEventStages.has(value);
}

export function isWebChatRenderDisplayKind(
  value: unknown,
): value is RealtimeRenderDisplayKind {
  return typeof value === 'string' && realtimeRenderDisplayKinds.has(value);
}

export function isWebChatRenderDisplayStageLabel(
  value: unknown,
): value is RealtimeRenderDisplayStageLabel {
  return (
    typeof value === 'string' && realtimeRenderDisplayStageLabels.has(value)
  );
}

export function isWebChatRealtimeAudienceScope(
  value: unknown,
): value is RealtimeAudienceScope {
  return typeof value === 'string' && realtimeAudienceScopes.has(value);
}

export function isWebChatRealtimeRenderState(
  value: unknown,
): value is RealtimeRenderState {
  return typeof value === 'string' && realtimeRenderStates.has(value);
}

export function isWebChatRealtimeRenderSeverity(
  value: unknown,
): value is RealtimeRenderSeverity {
  return typeof value === 'string' && realtimeRenderSeverities.has(value);
}

export interface WebChatPulseMetadataDecodeResult {
  readonly isValid: boolean;
  readonly pulse: WebChatPulseMetadata;
}

export function decodeWebChatPulseMetadata(
  data: unknown,
): WebChatPulseMetadataDecodeResult {
  const empty = emptyPulseMetadata();
  if (!isObjectRecord(data)) {
    return { isValid: true, pulse: empty };
  }

  if (hasCaseConflictingPulseMetadataProperty(data)) {
    return { isValid: false, pulse: empty };
  }

  const pulseKind = readNullableExactString(data, 'pulseKind');
  const pulseSource = readNullableExactString(data, 'pulseSource');
  const messageKey = readNullableExactString(data, 'messageKey');
  const locale = readNullableExactString(data, 'locale');
  const variantKey = readNullableExactString(data, 'variantKey');
  const isFallback = readOptionalBoolean(data, 'isFallback');
  const isDynamic = readOptionalBoolean(data, 'isDynamic');
  const silenceBudgetSeconds = readNullableNonnegativeSafeInteger(
    data,
    'silenceBudgetSeconds',
  );
  const elapsedSinceLastMeaningfulPulseMs = readNullableNonnegativeSafeInteger(
    data,
    'elapsedSinceLastMeaningfulPulseMs',
  );
  const operationKey = readNullableExactString(data, 'operationKey');
  const instructionId = readNullableExactString(data, 'instructionId');
  const correlationId = readNullableExactString(data, 'correlationId');
  if (
    pulseKind === undefined ||
    pulseSource === undefined ||
    messageKey === undefined ||
    locale === undefined ||
    variantKey === undefined ||
    isFallback === undefined ||
    isDynamic === undefined ||
    silenceBudgetSeconds === undefined ||
    elapsedSinceLastMeaningfulPulseMs === undefined ||
    operationKey === undefined ||
    instructionId === undefined ||
    correlationId === undefined ||
    (pulseKind !== null && !isConversationPulseKind(pulseKind)) ||
    (pulseSource !== null && !isConversationPulseSource(pulseSource)) ||
    (correlationId !== null && !isCanonicalUuid(correlationId))
  ) {
    return { isValid: false, pulse: empty };
  }

  return {
    isValid: true,
    pulse: {
      pulseKind,
      pulseSource,
      messageKey,
      locale,
      variantKey,
      isFallback,
      isDynamic,
      silenceBudgetSeconds,
      elapsedSinceLastMeaningfulPulseMs,
      operationKey,
      instructionId,
      correlationId,
    },
  };
}

function isConversationPulseKind(
  value: unknown,
): value is ConversationPulseKind {
  return typeof value === 'string' && conversationPulseKinds.has(value);
}

function isConversationPulseSource(
  value: unknown,
): value is ConversationPulseSource {
  return typeof value === 'string' && conversationPulseSources.has(value);
}

function hasCaseConflictingPulseMetadataProperty(
  data: Record<string, unknown>,
): boolean {
  return Object.keys(data).some((propertyName) => {
    const canonicalPropertyName = pulseMetadataPropertyNamesByFoldedName.get(
      propertyName.toLowerCase(),
    );
    return (
      canonicalPropertyName !== undefined &&
      canonicalPropertyName !== propertyName
    );
  });
}

function emptyPulseMetadata(): WebChatPulseMetadata {
  return {
    pulseKind: null,
    pulseSource: null,
    messageKey: null,
    locale: null,
    variantKey: null,
    isFallback: false,
    isDynamic: false,
    silenceBudgetSeconds: null,
    elapsedSinceLastMeaningfulPulseMs: null,
    operationKey: null,
    instructionId: null,
    correlationId: null,
  };
}

function readNullableExactString(
  source: Record<string, unknown>,
  propertyName: string,
): string | null | undefined {
  if (!Object.hasOwn(source, propertyName)) {
    return null;
  }

  const value = readRecordProperty(source, propertyName);
  if (value === null) {
    return null;
  }

  return typeof value === 'string' && value.length > 0 && value === value.trim()
    ? value
    : undefined;
}

function readOptionalBoolean(
  source: Record<string, unknown>,
  propertyName: string,
): boolean | undefined {
  if (!Object.hasOwn(source, propertyName)) {
    return false;
  }

  const value = readRecordProperty(source, propertyName);
  return typeof value === 'boolean' ? value : undefined;
}

function readNullableNonnegativeSafeInteger(
  source: Record<string, unknown>,
  propertyName: string,
): number | null | undefined {
  if (!Object.hasOwn(source, propertyName)) {
    return null;
  }

  const value = readRecordProperty(source, propertyName);
  if (value === null) {
    return null;
  }

  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
    ? value
    : undefined;
}

export function normalizeWebChatPresentations(
  data: unknown,
): PresentationEnvelopeContract[] {
  return [...decodeWebChatPresentations(data).presentations];
}

export function decodeWebChatPresentations(
  data: unknown,
): PresentationEnvelopeCollectionDecodeResult {
  return presentationEnvelopeCodec.decodeCollection(data);
}
